
import React, { useState, useContext, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { getAIGeneratedClue } from '../gemini';
// FIX: Changed import to correct PageContext file to avoid circular dependency
import { PageContext } from '../PageContext';
import { Page } from '../types';
import { urlToBase64 } from '../storageUtils';
import AIConsentModal from './AIConsentModal';

interface AIClueGeneratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onClueAccepted: (clue: string) => void;
    imageUrl: string;
    answer: string;
}

const AIClueGeneratorModal: React.FC<AIClueGeneratorModalProps> = ({ isOpen, onClose, onClueAccepted, imageUrl, answer }) => {
    const { userProfile, useAICredit } = useAuth();
    const pageContext = useContext(PageContext);
    const [generatedClue, setGeneratedClue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showConsentModal, setShowConsentModal] = useState(false);

    useEffect(() => {
        // Reset state when modal opens/closes
        if (!isOpen) {
            setGeneratedClue('');
            setIsLoading(false);
            setError('');
        }
    }, [isOpen]);

    const handleGenerate = async () => {
        if (!userProfile) return;

        if (!userProfile.aiConsentEnabled) {
            setShowConsentModal(true);
            return;
        }

        if (userProfile.aiCredits <= 0) {
            alert("You're out of AI Credits! Please visit the AI Creator Studio to get more.");
            pageContext?.switchPage(Page.AIHome, "AI Creator Studio");
            onClose();
            return;
        }

        setIsLoading(true);
        setError('');
        setGeneratedClue(''); // Clear previous clue

        const creditUsed = await useAICredit();
        if (creditUsed) {
            const imageBase64DataUrl = await urlToBase64(imageUrl);
            const newClue = await getAIGeneratedClue(answer, imageBase64DataUrl ?? undefined);
            if (newClue) {
                setGeneratedClue(newClue);
            } else {
                setError("The AI couldn't generate a clue. Please try again.");
                // Note: We might want to refund the credit here in a real app
            }
        } else {
            setError("Could not use an AI credit. Please try again.");
        }
        
        setIsLoading(false);
    };

    const handleUseClue = () => {
        onClueAccepted(generatedClue);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
            <div 
                className="bg-gray-800 text-white rounded-lg shadow-xl w-full max-w-md flex flex-col border border-white/20" 
                onClick={e => e.stopPropagation()}
            >
                <div className="p-4 border-b border-white/20">
                    <h2 className="text-xl font-bold aurora-text">✨ Generate Clue with AI</h2>
                    <p className="text-sm text-gray-400 mt-1">The AI will analyze your image and answer. This will use 1 AI Credit.</p>
                </div>

                <div className="p-4 space-y-4">
                    <div className="flex gap-4 items-start">
                        <img src={imageUrl} alt="Puzzle context" className="w-24 h-24 rounded-lg object-cover bg-black/20" />
                        <div className="flex-1">
                            <p className="text-sm text-gray-400">Answer</p>
                            <p className="font-bold text-lg bg-black/20 p-2 rounded-md">{answer}</p>
                        </div>
                    </div>
                    
                    {isLoading && (
                        <div className="text-center p-4">
                            <div className="spinner-white mx-auto"></div>
                            <p className="mt-2 text-sm text-gray-300">Generating clue...</p>
                        </div>
                    )}
                    
                    {error && <p className="text-red-400 text-center text-sm">{error}</p>}

                    {generatedClue && !isLoading && (
                        <div className="bg-black/20 p-3 rounded-md">
                            <p className="text-sm text-gray-400">AI Suggestion:</p>
                            <p className="font-semibold">{generatedClue}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-between items-center space-x-2 p-4 border-t border-white/20">
                    <button onClick={onClose} className="bg-transparent border border-white/50 text-white font-bold py-2 px-4 rounded-lg hover:bg-white/10">
                        Cancel
                    </button>
                    {generatedClue ? (
                        <div className="flex gap-2">
                            <button onClick={handleGenerate} disabled={isLoading} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-500">
                                Try Again
                            </button>
                            <button onClick={handleUseClue} className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-2 px-4 rounded-lg">
                                Use this Clue
                            </button>
                        </div>
                    ) : (
                        <button onClick={handleGenerate} disabled={isLoading} className="bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold py-2 px-4 rounded-lg min-w-[120px] flex justify-center items-center">
                            Generate
                        </button>
                    )}
                </div>
            </div>

            <AIConsentModal 
                isOpen={showConsentModal} 
                onClose={() => setShowConsentModal(false)} 
                onConsentGranted={handleGenerate} 
            />
        </div>
    );
};

export default AIClueGeneratorModal;
